import {type TradingAccount} from './account';
import {type SingleAlgoFullReport} from './algo/fe-algo';
import {type OrderType, type Symbol, type TimeInForce} from './common';
import type {
  Direction,
  OrderRecord,
  OrderTypes,
} from './order';

export enum ComponentType {
  NONE = 'none',
  ORDER_ENTRY = 'orderEntry',
  ALGO_MONITOR = 'monitoringScreen',
  OPTIONS_CURVE = 'optionsCurve',
  TICKER_GRID = 'tickerGrid',
}

export enum LinkerDataType {
  ALGO = 'algo',
  ORDER = 'order',
  ORDER_LOOKUP = 'orderLookup',
  ALGO_MODIFY = 'algoModify',
  ALGO_CREATE = 'algoCreate',
  ORDER_CREATE = 'orderCreate',
  OPTIONS = 'options',
  SYMBOL = 'symbol',
}

export type AlgoShareData = {
  algoRunId: string;
  algoName?: string;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export type QEPSharePublishData<T extends LinkerDataType, K extends OrderTypes | undefined = undefined> = {
  componentType: ComponentType;
  dataType: T;
  data: T extends (LinkerDataType.SYMBOL)
    ? Symbol
    : T extends (LinkerDataType.ALGO | LinkerDataType.ALGO_MODIFY)
      ? AlgoShareData
      : T extends (LinkerDataType.ALGO_CREATE | LinkerDataType.ORDER_CREATE)
        ? undefined
        : T extends LinkerDataType.ORDER_LOOKUP
          ? {orderId: string}
          : OrderRecord;
  meta: T extends (LinkerDataType.ALGO | LinkerDataType.ALGO_MODIFY)
    ? AlgoCloneMetaData
    : T extends LinkerDataType.ALGO_CREATE
      ? AlgoCreateMetaData
      : T extends LinkerDataType.ORDER_CREATE
        ? OrderCreateMetaData<K>
        : undefined;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export type QEPShareSubscribeData<T extends LinkerDataType, K extends OrderTypes | undefined = undefined> = {
  type: T;
  data: T extends (LinkerDataType.SYMBOL)
    ? Symbol
    : T extends (LinkerDataType.ALGO | LinkerDataType.ALGO_MODIFY)
      ? Partial<SingleAlgoFullReport>
      : T extends LinkerDataType.ORDER
        ? OrderRecord
        : T extends LinkerDataType.ORDER_LOOKUP
          ? OrderRecord
          : undefined;
  meta: T extends (LinkerDataType.ALGO | LinkerDataType.ALGO_MODIFY)
    ? AlgoCloneMetaData
    : T extends LinkerDataType.ALGO_CREATE
      ? AlgoCreateMetaData
      : T extends LinkerDataType.ORDER_CREATE
        ? OrderCreateMetaData<K>
        : undefined;
  sourceWindowId?: string;
  sourceComponentId?: string;
};

/** Identifies one submitted algo leg so linked consumers can preserve launch order. */
export type AlgoOriginalLegOrderItem = Pick<Symbol, 'symbol' | 'segmentMic' | 'exchangeSymbol'>;

export type AlgoCloneMetaData = {
  completeRemainder?: boolean;
  modifyAlgo?: boolean; // Not an actual clone, but the Linker will have to resolve this to an algo and pass the algo information along as data
  originalLegOrder?: AlgoOriginalLegOrderItem[];
};

export type OrderCreateMetaData<T extends OrderTypes | undefined = undefined> = {
  ticketType?: OrderTypes;
  limitPrice?: number;
  stopLimit?: number;
  orderType?: T extends OrderTypes.Manual ? OrderType | undefined : undefined;
  timeInForce?: T extends OrderTypes.Manual ? TimeInForce | undefined : undefined;
  displayQuantity?: T extends OrderTypes.Manual ? number | undefined : undefined;
  symbols: Array<Symbol & {direction?: Direction; qty?: number; account?: TradingAccount; legs?: OrderRecord[]; legsRoots?: OrderRecord[]}>;
};
export type AlgoCreateMetaData = {
  startTime?: string;
  endTime?: string;
  duration?: number;
} & OrderCreateMetaData<OrderTypes.Algo | OrderTypes.Spread>;

export enum WindowTypes {
  WINDOW = 'window',
  COMPONENT = 'component',
}

export type LinkedWindow = {
  windowId: string;
  windowName: string;
};
